import type { FieldDefinition } from "@/types/template";

/** Hard ceiling so a typo'd timing value can't queue an absurd render:
 *  one hour at 60fps. */
export const MAX_DURATION_FRAMES = 216_000;

const UNIT_SECONDS: Record<string, number> = {
  seconds: 1,
  minutes: 60,
};

/** Resolve duration in frames for the current field values.
 *
 *  Preferred path: fields annotated with `timing` in the schema declare
 *  exactly how they map to runtime — each contributes
 *  (value × unit × scale + offsetSeconds) seconds, contributions sum, so a
 *  minutes+seconds pair or "count × seconds-per-item" both work.
 *
 *  Legacy path (no annotations): guess by common field names Claude used to
 *  generate (durationFrames, displayDuration, duration, durationSeconds).
 *
 *  @param fieldValues - Current prop values from the component
 *  @param fps - Frames per second for seconds→frames conversion
 *  @param fallback - Used when nothing resolves (defaults to 150)
 *  @param fields - Field schema; enables the explicit timing path
 */
export function resolveDurationFrames(
  fieldValues: Record<string, unknown>,
  fps: number,
  fallback = 150,
  fields?: FieldDefinition[],
): number {
  const safeFps = fps > 0 ? fps : 30;

  // Explicit schema-declared timing wins over any name guessing.
  const timingFields = (fields ?? []).filter((f) => f.timing);
  if (timingFields.length > 0) {
    let frames = 0;
    for (const f of timingFields) {
      const t = f.timing!;
      const raw = fieldValues[f.key] ?? f.default;
      const value = typeof raw === "number" ? raw : Number(raw);
      const scale = t.scale ?? 1;
      const offset = (t.offsetSeconds ?? 0) * safeFps;
      if (t.unit === "frames") {
        frames += (Number.isFinite(value) ? value : 0) * scale + offset;
      } else {
        const unitSec = UNIT_SECONDS[t.unit] ?? 1;
        frames +=
          (Number.isFinite(value) ? value : 0) * unitSec * scale * safeFps +
          offset;
      }
    }
    const rounded = Math.round(frames);
    if (rounded > 0) return Math.min(rounded, MAX_DURATION_FRAMES);
    return fallback;
  }

  // Legacy heuristic for schemas that predate timing annotations.
  if (
    typeof fieldValues.durationFrames === "number" &&
    fieldValues.durationFrames > 0
  ) {
    return Math.min(fieldValues.durationFrames, MAX_DURATION_FRAMES);
  }
  for (const key of ["displayDuration", "duration", "durationSeconds"]) {
    const val = fieldValues[key];
    if (typeof val === "number" && val > 0) {
      return Math.min(Math.round(val * safeFps), MAX_DURATION_FRAMES);
    }
  }
  return fallback;
}
